<template>
  <div>
    <b-row>
      <b-col lg="12">
        <!-- Header -->
        <div>
          <b-link
            class="header-link"
            :to="`/wim-detail?wid=${this.$route.query.wid}`"
          >
            <feather-icon
              class="back-icon"
              size="26"
              icon="ArrowLeftIcon"
            />
            <span class="header-text">
              รายละเอียดข้อมูลรถเข้าชั่งหนัก WIM ({{ StationName }})
            </span>
          </b-link>
        </div>

        <!-- Main Content Card -->
        <b-card-code>
          <div
            v-if="loading"
            class="loading-container"
          >
            <b-spinner
              variant="primary"
              label="Loading..."
            />
            <span class="loading-text">กำลังโหลดข้อมูล...</span>
          </div>

          <div
            v-else-if="error"
            class="error-container"
          >
            {{ error }}
            <b-button
              size="sm"
              variant="outline-primary"
              @click="getHistory"
            >
              ลองใหม่
            </b-button>
          </div>

          <b-row v-else>
            <!-- Date Filter -->
            <b-col
              md="4"
              class="d-flex align-items-center justify-content-start"
            >
              <label class="date-label">จาก</label>
              <b-form-group class="date-group">
                <flat-pickr
                  v-model="date.dateFrom"
                  class="form-control"
                  :config="dateConfig"
                  @input="getHistory"
                />
              </b-form-group>
              <label class="date-label">ถึง</label>
              <b-form-group>
                <flat-pickr
                  v-model="date.dateTo"
                  class="form-control"
                  :config="dateConfig"
                  @input="getHistory"
                />
              </b-form-group>
            </b-col>

            <!-- Search -->
            <b-col
              md="4"
              class="text-sm-right mb-1"
            />
            <b-col
              md="4"
              class="d-flex align-items-center justify-content-end mb-1"
            >
              <label class="mb-0 me-2">ค้นหา</label>
              <b-input-group
                size="sm"
                class="search-group w-50"
              >
                <b-form-input
                  id="filterInput"
                  v-model="filter"
                  type="search"
                  placeholder="Type to Search"
                />
                <b-input-group-append>
                  <b-button
                    :disabled="!filter"
                    @click="filter = ''"
                  >
                    ล้าง
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-col>

            <!-- Table -->
            <b-col cols="12">
              <b-table
                id="table"
                ref="wimTable"
                responsive
                :busy="tableLoading"
                :items="items"
                :fields="computedFields"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :sort-direction="sortDirection"
                :filter="filter"
                :filter-included-fields="filterOn"
                @filtered="onFiltered"
              >
                <!-- <template #cell(wimdetail)="data">
                  <b-link
                    v-b-modal="'Wimdetail'"
                    @click="$refs.wimdetail.getFormulaPopup(data.item.camid), title(data.item.Camera_Desc)"
                  ><feather-icon
                    class="text-info"
                    icon="TrelloIcon"
                    size="20"
                  />
                  </b-link>
                </template> -->
                <template #table-busy>
                  <div class="text-center my-2">
                    <b-spinner class="align-middle" />
                    <strong>กำลังโหลด...</strong>
                  </div>
                </template>
                <template #cell(modal)="data">
                  <b-link
                    v-b-modal="'Wimdetail'"
                    class="mr-1"
                    @click="$refs.Wimdetail.sendInfo(data.item)"
                  >
                    <feather-icon
                      icon="InfoIcon"
                      size="18"
                    />
                  </b-link>
                </template>
                <template #cell(IsOverWeight)="data">
                  <b-badge
                    style="font-size: 13px;"
                    :variant="IsOverWeight[1][data.value]"
                  >
                    {{ IsOverWeight[0][data.value] }}
                  </b-badge><br>
                  <span style="font-size: 10px;"> {{ data.item.IsOverWeight }}</span>
                </template>
                <template #cell(VehicleClass)="data">
                  <img
                    :src="`https://wts.drr.go.th/images/misc/truck-img/truck-type/${formatVehicleClass(data.value)}.svg`"
                    :alt="`Vehicle Class ${data.value}`"
                    class="vehicle-icon"
                    height="30"
                  >
                </template>
                <template #cell(Image01Name)="data">
                  <b-img
                    :src="data.item.Image01Name"
                    width="100px"
                    fluid
                    @click="showImg(data.item.Image01Name)"
                  />
                </template>
                <template #cell(Image02Name)="data">
                  <b-img
                    v-b-modal="'Wimdetail'"
                    :src="data.item.Image02Name"
                    width="100px"
                    fluid
                    @click="$refs.Wimdetail.sendInfo(data.item)"
                  />
                </template>
                <template #cell(LPHeadNo)="data">
                  <span
                    style="font-size: 13px;"
                  >
                    {{ data.item.LPHeadNo }} <br> {{ data.item.ProvinceName }}
                  </span>
                </template>
                <template #cell(VehicleClassDesc2)="data">
                  <span
                    style="font-size: 13px;"
                  >
                    {{ data.item.VehicleClassDesc2 }}: {{ data.item.VehicleClassDesc3 }}
                  </span>
                </template>
                <template #cell(GrossWeight)="data">
                  <span
                    :style="{ color: data.item.IsOverWeight === 'เกินพิกัด' ? 'red' : 'inherit', fontSize: '13px' }"
                  >
                    {{ data.item.GrossWeight }} ตัน
                    <br><span style="color: #96fff1"> น้ำหนักกฎหมาย {{ data.item.LegalWeight }} ตัน </span>
                  </span>
                </template>
                <!-- Existing cell templates... -->

              </b-table>
            </b-col>

            <!-- Pagination -->
            <b-col cols="12">
              <b-card-body class="d-flex justify-content-between flex-wrap pt-1">
                <b-form-group
                  label="Per Page"
                  label-cols="6"
                  label-align="left"
                  label-size="sm"
                  class="text-nowrap mb-md-0 mr-1"
                >
                  <b-form-select
                    v-model="perPage"
                    size="sm"
                    inline
                    :options="pageOptions"
                  />
                </b-form-group>

                <div>
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    first-number
                    last-number
                    class="pagination-nav"
                  >
                    <template #prev-text>
                      <feather-icon
                        icon="ChevronLeftIcon"
                        size="18"
                      />
                    </template>
                    <template #next-text>
                      <feather-icon
                        icon="ChevronRightIcon"
                        size="18"
                      />
                    </template>
                  </b-pagination>
                </div>
              </b-card-body>
            </b-col>
          </b-row>
        </b-card-code>
      </b-col>
    </b-row>
    <template>
      <!-- MODAL ProblemEdit -->
      <WIMModal
        ref="Wimdetail"
        :title="titleName"
      />
    </template>
  </div>
</template>

<script>
/* eslint-disable global-require */
import {
  BRow,
  BCol,
  BTable,
  BFormGroup,
  BBadge,
  BImg,
  BFormSelect,
  BPagination,
  BCardBody,
  BLink,
  BButton,
  BInputGroupAppend,
  BInputGroup,
  BFormInput,
  VBModal,
  BSpinner,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import Ripple from 'vue-ripple-directive'
import axios from '@axios'
import BCardCode from '@core/components/b-card-code/BCardCode.vue'
import WIMModal from './WIMModal.vue'

export default {
  name: 'WIMTable',

  components: {
    BRow,
    BCol,
    BTable,
    BFormGroup,
    BBadge,
    BImg,
    BFormSelect,
    BPagination,
    BCardBody,
    BButton,
    BLink,
    BInputGroupAppend,
    BInputGroup,
    BFormInput,
    BCardCode,
    BSpinner,
    flatPickr,
    WIMModal,
  },

  directives: {
    'b-modal': VBModal,
    Ripple,
  },

  data() {
    return {
      loading: false,
      tableLoading: false,
      error: null,
      updateInterval: null,
      items: [],
      StationName: '',
      date: {
        wid: this.$route.query.wid,
        dateFrom: '',
        dateTo: '',
      },
      dateConfig: {
        dateFormat: 'Y-m-d',
        allowInput: true,
      },
      fields: [
        {
          key: 'TimeStamp',
          label: 'วันที่เวลา',
          sortable: true,
          class: 'text-center',
        },
        {
          key: 'VehicleClassDesc2',
          label: 'ประเภท',
          sortable: true,
          class: 'text-center',
        },
        {
          key: 'VehicleClass',
          label: 'รูปประเภท',
          sortable: true,
          class: 'text-center',
        },
        {
          key: 'GrossWeight',
          label: 'น้ำหนักที่ชั่ง',
          sortable: true,
          class: 'text-center',
        },
        {
          key: 'LPHeadNo',
          label: 'ทะเบียน',
          sortable: true,
          class: 'text-center',
        },
        {
          key: 'Image02Name',
          label: 'รูปทะเบียน',
          sortable: true,
          class: 'text-center',
        },
        {
          key: 'Image01Name',
          label: 'รูปภาพ',
          sortable: true,
          class: 'text-center',
        },
        {
          key: 'IsOverWeight',
          label: 'น้ำหนัก',
          sortable: true,
          class: 'text-center',
        },
        {
          key: 'modal',
          label: 'รายละเอียด',
          sortable: true,
          class: 'text-center',
        },
      ],
      perPage: 25,
      pageOptions: [25, 50, 100],
      totalRows: 1,
      currentPage: 1,
      sortBy: '',
      sortDesc: false,
      sortDirection: 'asc',
      filter: null,
      filterOn: [],
      IsOverWeight: [{
        เกินพิกัด: 'OVER-GVW',
        ไม่เกินพิกัด: 'PASS',
      },
      {
        เกินพิกัด: 'light-danger',
        ไม่เกินพิกัด: 'light-success',
      }],
      userData: JSON.parse(localStorage.getItem('userData')),
    }
  },

  computed: {
    sortOptions() {
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
    computedFields() {
      if (this.userData.role === 'User') return this.fields.filter(field => !field.requiresAdmin)
      return this.fields
    },
  },

  created() {
    this.startAutoUpdate()
  },

  mounted() {
    this.initializeData()
  },

  beforeDestroy() {
    this.stopAutoUpdate()
  },

  methods: {
    initializeData() {
      this.setToday()
      this.getHistory()
    },

    formatVehicleClass(value) {
      // แปลงค่าเป็นตัวเลข 3 หลัก เช่น 1 เป็น 001, 12 เป็น 012
      return value.toString().padStart(3, '0')
    },

    startAutoUpdate() {
      this.updateInterval = setInterval(() => {
        this.getHistory()
      }, 300000)
    },

    stopAutoUpdate() {
      if (this.updateInterval) {
        clearInterval(this.updateInterval)
      }
    },

    setToday() {
      const today = new Date()
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
      this.date.dateFrom = firstDay.toISOString().slice(0, 10)
      this.date.dateTo = today.toISOString().slice(0, 10)
    },

    async getHistory() {
      if (this.loading) return

      this.loading = true
      this.tableLoading = true
      this.error = null

      try {
        const response = await axios.post(
          'https://wts.drr.go.th/node-red/get-Table-WIM',
          this.date,
          {
            headers: {
              'Content-Type': 'application/json',
            },
            timeout: 30000,
          },
        )

        this.items = response.data
        this.totalRows = response.data.length
        this.StationName = response.data[0]?.StationName || ''
      } catch (error) {
        console.error('Error fetching WIM data:', error)
        this.error = 'ไม่สามารถโหลดข้อมูลได้'
        setTimeout(() => {
          this.getHistory()
        }, 30000)
      } finally {
        this.loading = false
        this.tableLoading = false
      }
    },

    async showImg(img) {
      try {
        await this.$swal({
          imageUrl: img,
          imageAlt: 'Vehicle Image',
          width: 800,
          background: 'hsla(0, 100%, 100%, 0)',
          showCancelButton: false,
          showConfirmButton: false,
          showCloseButton: true,
        })
      } catch (error) {
        console.error('Error showing image:', error)
      }
    },

    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length
      this.currentPage = 1
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-flatpicker.scss';
@import '@core/scss/vue/libs/vue-select.scss';
@import url('https://fonts.googleapis.com/css2?family=Sarabun&display=swap');

.header-link {
  font-size: 26px;
  color: #fff;

  .back-icon {
    margin-bottom: 5px;
  }

  .header-text {
    font-size: 20px;
    color: #ffffff;
  }
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  gap: 1rem;
}

.date-label {
  padding-right: 14px;
  font-size: 15px;
}

.date-group {
  padding-right: 14px;
}

.search-group {
  padding-left: 14px;
}

.table {
  th, td {
    border: 1px solid #000000;
    text-align: left;
    padding: 8px;
  }
}

.pagination-nav {
  margin-bottom: 0;
}
</style>
